import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link, useNavigate } from 'react-router-dom';
import { ChevronRight, Coins, MoreVertical, Pencil, Trash2, X } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { ModalOverlay } from './ModalOverlay';
import { useEliminarCamada } from '@/lib/queries/camadas';
import { extractApiMessage } from '@/lib/api';
import { fmtFecha, fmtInt } from '@/lib/format';
import { formatoDecimal } from '@/lib/liquidacion';
import type { Camada, CamadaDetalle, Page } from '@/lib/types';
import { useAuthStore } from '@/stores/auth';

/** Datos complementarios y acciones de la camada seleccionada, sin otra pantalla resumen. */
export function CamadaManagement({
  detalle,
  canManage,
  canSeeFinance,
}: {
  detalle: CamadaDetalle;
  canManage: boolean;
  canSeeFinance: boolean;
}) {
  const { camada, kpis } = detalle;
  const [panel, setPanel] = useState<'options' | 'warning' | 'confirm' | null>(null);
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [removing, setRemoving] = useState(false);
  const eliminar = useEliminarCamada();
  const deleting = removing || eliminar.isPending;
  const client = useQueryClient();
  const ownerId = useAuthStore((state) => state.user?.id ?? null);
  const navigate = useNavigate();
  const mounted = useRef(false);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);
  const canDelete =
    canManage &&
    !camada.cerrada &&
    !detalle.liquidacion &&
    !detalle.lotes.length &&
    camada.num_animales_total === 0 &&
    detalle.partes_count === 0 &&
    detalle.piensos_count === 0;

  function close() {
    if (deleting) return;
    setPanel(null);
    setCode('');
    setError(null);
  }

  async function remove() {
    if (!canDelete || code.trim() !== camada.codigo_lote || deleting) return;
    setRemoving(true);
    try {
      await eliminar.mutateAsync({ id: camada.id });
      const owned = (query: { queryKey: readonly unknown[] }) =>
        query.queryKey.at(-2) === 'owner' && query.queryKey.at(-1) === ownerId;
      client.setQueriesData<Page<Camada>>({ queryKey: ['camadas'], predicate: owned }, (data) => {
        if (!data || !Array.isArray(data.items)) return data;
        const items = data.items.filter((item) => item.id !== camada.id);
        return items.length === data.items.length
          ? data
          : { ...data, items, total: Math.max(0, data.total - (data.items.length - items.length)) };
      });
      await client.invalidateQueries({ queryKey: ['camadas'] });
      if (mounted.current) {
        close();
        navigate('/camadas', { replace: true });
      }
      client.removeQueries({ queryKey: ['camadas', camada.id], predicate: owned });
    } catch (err) {
      if (mounted.current) setError(extractApiMessage(err));
    } finally {
      if (mounted.current) setRemoving(false);
    }
  }

  const title =
    panel === 'options'
      ? `Camada ${camada.codigo_lote}`
      : panel === 'warning'
        ? 'Eliminar camada vacía'
        : 'Confirmar eliminación';

  return (
    <>
      <button
        type="button"
        className="camadas-options"
        aria-label={`Opciones de ${camada.codigo_lote}`}
        aria-haspopup="dialog"
        aria-expanded={panel !== null}
        onClick={() => setPanel('options')}
      >
        <MoreVertical size={22} aria-hidden />
      </button>
      {panel &&
        createPortal(
          <ModalOverlay
            placement="bottom"
            className="max-w-md text-text-primary"
            onBackdrop={close}
            ariaLabelledby="camada-options-title"
          >
            <header className="mb-3 flex items-center justify-between gap-3">
              <h2 id="camada-options-title" className="ui-page-title min-w-0 break-words">
                {title}
              </h2>
              <button
                type="button"
                className="ui-icon-button ui-close-button"
                aria-label="Cerrar opciones"
                disabled={deleting}
                onClick={close}
              >
                <X size={22} aria-hidden />
              </button>
            </header>
            {panel === 'options' ? (
              <>
                <p className="mb-3 text-sm text-text-secondary">
                  {camada.fase === 'cebo' ? 'Cebo' : 'Recría'} · Entrada{' '}
                  {fmtFecha(camada.fecha_entrada)}
                </p>
                <dl className="camadas-balance">
                  <div>
                    <dt>Entradas</dt>
                    <dd>{fmtInt(kpis.aves_iniciales)}</dd>
                  </div>
                  <div>
                    <dt>Bajas acumuladas</dt>
                    <dd>{fmtInt(kpis.bajas_acumuladas)}</dd>
                  </div>
                  <div>
                    <dt>IC</dt>
                    <dd>
                      {kpis.indice_conversion == null
                        ? '—'
                        : formatoDecimal(kpis.indice_conversion, 2)}
                    </dd>
                  </div>
                </dl>
                {canManage && !camada.cerrada && (
                  <Link className="ui-menu-row" to={`/camadas/${camada.id}/editar`} onClick={close}>
                    <Pencil size={22} aria-hidden />
                    <span>Editar datos</span>
                    <ChevronRight size={18} aria-hidden />
                  </Link>
                )}
                {canSeeFinance && detalle.liquidacion && (
                  <Link className="ui-menu-row" to={`/liquidacion/${camada.id}`} onClick={close}>
                    <Coins size={22} aria-hidden />
                    <span>Liquidación guardada</span>
                    <ChevronRight size={18} aria-hidden />
                  </Link>
                )}
                {canDelete && (
                  <button
                    type="button"
                    className="ui-menu-row text-warning-text"
                    onClick={() => setPanel('warning')}
                  >
                    <Trash2 size={22} aria-hidden />
                    <span>Eliminar camada vacía</span>
                    <ChevronRight size={18} aria-hidden />
                  </button>
                )}
              </>
            ) : (
              <>
                <p className="mb-4 text-sm text-text-secondary">
                  {panel === 'warning'
                    ? `Se eliminará la camada ${camada.codigo_lote}, sin animales ni registros. Confirma únicamente si la creaste por error.`
                    : `Escribe ${camada.codigo_lote} para confirmar la eliminación.`}
                </p>
                {panel === 'confirm' && (
                  <label className="mb-4 block text-sm">
                    Código de la camada
                    <input
                      className="ui-field mt-2"
                      value={code}
                      onChange={(event) => setCode(event.target.value)}
                      autoComplete="off"
                      disabled={deleting}
                    />
                  </label>
                )}
                {error && (
                  <p role="alert" className="mb-3 text-sm text-warning-text">
                    {error}
                  </p>
                )}
                <div className="flex gap-3">
                  <button
                    type="button"
                    className="ui-secondary flex-1"
                    disabled={deleting}
                    onClick={close}
                  >
                    Cancelar
                  </button>
                  <button
                    type="button"
                    className="ui-secondary flex-1 text-warning-text"
                    disabled={
                      deleting || (panel === 'confirm' && code.trim() !== camada.codigo_lote)
                    }
                    onClick={() => (panel === 'warning' ? setPanel('confirm') : void remove())}
                  >
                    {deleting
                      ? 'Eliminando…'
                      : panel === 'warning'
                        ? 'Continuar'
                        : 'Eliminar definitivamente'}
                  </button>
                </div>
              </>
            )}
          </ModalOverlay>,
          document.body,
        )}
    </>
  );
}
